import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import { getMyUniversity } from '../../api/university';
import { getCurrentUser } from '../../services/auth';
import { groupAPI, learningAPI } from '../../services/api';
import { isUniversityAdmin } from '../../utils/role';
import {
  TeachingAnnouncements,
  TeachingAssignments,
  TeachingCohorts,
  TeachingOverview,
  TeachingStudents,
} from '../../components/Learning/TeachingWorkspaceSections';

const tabKeys = ['overview', 'students', 'cohorts', 'assignments', 'announcements'];

function TeachingWorkspacePage() {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = tabKeys.indexOf(searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState(requestedTab >= 0 ? requestedTab : 0);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadWorkspace = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const [studentsResponse, groupsResponse, activeAssignmentsResponse, archivedAssignmentsResponse, announcementsResponse, universityResponse] = await Promise.all([
        learningAPI.listStudents(),
        groupAPI.list(),
        learningAPI.listAssignments({ status: 'active' }),
        learningAPI.listAssignments({ status: 'archived' }),
        learningAPI.listAnnouncements(),
        getMyUniversity(),
      ]);
      setStudents(studentsResponse.data?.students || []);
      setGroups(groupsResponse.data || []);
      setAssignments([
        ...(activeAssignmentsResponse.data?.assignments || []),
        ...(archivedAssignmentsResponse.data?.assignments || []),
      ]);
      setAnnouncements(announcementsResponse.data?.announcements || []);
      setUniversity(universityResponse.data || null);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load the teaching workspace.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspace({ showLoading: true });
  }, [loadWorkspace]);

  const changeTab = (nextTab) => {
    setActiveTab(nextTab);
    const key = tabKeys[nextTab];
    setSearchParams(key === 'overview' ? {} : { tab: key }, { replace: true });
  };

  const primaryColor = university?.settings?.colors?.primary || '#146C94';

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 4 } }}>
      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 2.5,
          borderRadius: 4,
          color: '#fff',
          background: `linear-gradient(135deg, ${primaryColor}, #123d67)`,
          boxShadow: `0 22px 56px ${alpha(primaryColor, 0.22)}`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            {university?.logo && (
              <Box
                component="img"
                src={university.logo}
                alt={`${university.name} logo`}
                sx={{ width: 54, height: 54, objectFit: 'contain', bgcolor: '#fff', borderRadius: 2, p: 0.75 }}
              />
            )}
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.78, fontWeight: 800, letterSpacing: '0.12em' }}>
                {university?.name || 'University'}
              </Typography>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.7rem' } }}>
                Teaching workspace
              </Typography>
              <Typography sx={{ mt: 0.75, maxWidth: 720, opacity: 0.86 }}>
                Monitor learner progress, organise cohorts, assign practice, and send announcements.
              </Typography>
            </Box>
          </Stack>
          {isUniversityAdmin(currentUser) && (
            <Stack direction="row" spacing={1}>
              <Button
                component={RouterLink}
                to="/university/settings"
                variant="outlined"
                sx={{ color: '#fff', borderColor: alpha('#fff', 0.5), '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) } }}
              >
                Branding & settings
              </Button>
              <Button
                component={RouterLink}
                to="/university/admin"
                startIcon={<ManageAccountsRoundedIcon />}
                sx={{ bgcolor: '#fff', color: '#123d67', '&:hover': { bgcolor: alpha('#fff', 0.9) } }}
              >
                Admin console
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_event, nextTab) => changeTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Teaching workspace sections"
          sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<DashboardRoundedIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<PeopleAltRoundedIcon />} iconPosition="start" label="Students" />
          <Tab icon={<GroupsRoundedIcon />} iconPosition="start" label="Cohorts" />
          <Tab icon={<ChecklistRoundedIcon />} iconPosition="start" label="Assignments" />
          <Tab icon={<CampaignRoundedIcon />} iconPosition="start" label="Announcements" />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => loadWorkspace({ showLoading: true })}>Retry</Button>}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ minHeight: 340, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeTab === 0 && (
                <TeachingOverview
                  students={students}
                  groups={groups}
                  assignments={assignments}
                  announcements={announcements}
                  onSelectTab={changeTab}
                />
              )}
              {activeTab === 1 && <TeachingStudents students={students} />}
              {activeTab === 2 && <TeachingCohorts groups={groups} onRefresh={loadWorkspace} />}
              {activeTab === 3 && <TeachingAssignments assignments={assignments} groups={groups} students={students} onRefresh={loadWorkspace} />}
              {activeTab === 4 && <TeachingAnnouncements announcements={announcements} groups={groups} students={students} onRefresh={loadWorkspace} />}
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default TeachingWorkspacePage;
